import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import {
  User,
  Lock,
  Shield,
  Settings as SettingsIcon,
  BadgeCheck,
  CalendarDays,
  AlertCircle,
  KeyRound,
  Mail,
  Phone,
  MessageCircle,
  Building2,
  Hash,
  Globe,
  MapPin,
  Save,
} from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { setPassword } from "../../services/login";
import { useUser } from "../../context/UserContext";
import {
  FormModal,
  Form,
  Field,
  FormSubmit,
  AddressFields,
} from "../../components/form";
import { OutlineButton, PrimaryButton } from "../../components/Buttons";
import CustomInput from "../../components/CustomInput";

/**
 * Interior Designer Settings — unified page replacing the legacy
 * InteriorDesignerProfile.jsx. Reads like every other Settings surface in
 * the app: left-rail nav, slate-gradient SectionCard headers, validated
 * forms via the canonical primitives.
 *
 * Sections:
 *   • profile  — name, contacts, company info, address, bio, etc.
 *   • password — change password with a strength rubric
 *   • security — persona/account state, no project-scoped RBAC (IDs don't
 *                participate in per-project permissions)
 *
 * RBAC notes:
 *   Interior Designer is a "limited-settings" persona (no project access,
 *   no per-project permissions, no developer admin powers). The Security
 *   section reflects this — it surfaces the account status, persona, and
 *   designer ID rather than the project-permissions matrix that developer-
 *   side users see.
 */
const SECTIONS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "password", label: "Change password", icon: Lock },
  { id: "security", label: "Security", icon: Shield },
];

const profileSchema = yup.object({
  name: yup.string().trim().required("Name is required"),
  mobile: yup
    .string()
    .trim()
    .required("Mobile is required")
    .matches(/^\+?[0-9][0-9\s\-()]{6,}$/, "Enter a valid phone number"),
  email: yup
    .string()
    .trim()
    .required("Email is required")
    .email("Enter a valid email"),
  companyName: yup.string().trim().nullable(),
  gstNumber: yup.string().trim().nullable(),
  websiteUrl: yup
    .string()
    .trim()
    .nullable()
    .url("Enter a valid URL (https://…)")
    .transform((v) => (v === "" ? null : v)),
  yearsExperience: yup
    .number()
    .nullable()
    .typeError("Years of experience must be a number")
    .transform((v, orig) => (orig === "" || orig === null ? null : v)),
  specialization: yup.string().trim().nullable(),
  selfDescription: yup.string().trim().nullable(),
  socialLinkedin: yup.string().trim().nullable(),
  socialInstagram: yup.string().trim().nullable(),
  address: yup
    .object({
      buildingNo: yup.string().trim().nullable(),
      streetName1: yup.string().trim().nullable(),
      streetName2: yup.string().trim().nullable(),
      city: yup.string().trim().nullable(),
      district: yup.string().trim().nullable(),
      state: yup.string().trim().nullable(),
      pinCode: yup.string().trim().nullable(),
      country: yup.string().trim().nullable(),
    })
    .default({}),
});

const SectionCard = ({ title, subtitle, action, children }) => (
  <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
    <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-600 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-white">{title}</h2>
        {subtitle && <p className="text-xs text-slate-300 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="px-6 py-6 space-y-5">{children}</div>
  </section>
);

const InfoRow = ({ icon: Icon, label, value, span = false }) => (
  <div className={`flex items-start gap-3 ${span ? "md:col-span-2" : ""}`}>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-700">
      <Icon size={16} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </p>
      <p className="text-sm font-semibold text-slate-900 mt-0.5 break-words">
        {value || <span className="text-slate-400 font-normal">Not set</span>}
      </p>
    </div>
  </div>
);

function InteriorDesignerSettings() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("profile");

  // Hash drives the active section so deep links + refreshes land on the
  // right tab. Defaults to "profile" for a freshly-opened settings page.
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (SECTIONS.some((s) => s.id === hash)) {
      setActiveSection(hash);
    } else {
      setActiveSection("profile");
    }
  }, [location.hash]);

  const handleSectionChange = (id) => {
    setActiveSection(id);
    navigate(`#${id}`, { replace: true });
  };

  /* --- Profile data --- */
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const loadProfile = async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await axiosInstance.get("/interior-designers/profile");
      setProfile(res);
    } catch (err) {
      console.error("Error loading designer profile:", err);
      setProfileError(err?.response?.data?.detail || err.message || "Failed to load");
    } finally {
      setProfileLoading(false);
    }
  };
  useEffect(() => {
    loadProfile();
  }, []);

  const profileDefaults = useMemo(
    () => ({
      name: profile?.name || "",
      mobile: profile?.mobile || "",
      email: profile?.email || "",
      companyName: profile?.companyName || "",
      gstNumber: profile?.gstNumber || "",
      websiteUrl: profile?.websiteUrl || "",
      yearsExperience: profile?.yearsExperience ?? "",
      specialization: profile?.specialization || "",
      selfDescription: profile?.selfDescription || "",
      socialLinkedin: profile?.socialMediaUrls?.linkedin || "",
      socialInstagram: profile?.socialMediaUrls?.instagram || "",
      address: {
        buildingNo: profile?.address?.buildingNo || "",
        streetName1: profile?.address?.streetName1 || "",
        streetName2: profile?.address?.streetName2 || "",
        city: profile?.address?.city || "",
        district: profile?.address?.district || "",
        state: profile?.address?.state || "",
        pinCode: profile?.address?.pinCode || "",
        country: profile?.address?.country || "",
      },
    }),
    [profile]
  );

  const handleProfileSave = async (values) => {
    try {
      const payload = {
        name: values.name,
        mobile: values.mobile,
        email: values.email,
        companyName: values.companyName || null,
        gstNumber: values.gstNumber || null,
        websiteUrl: values.websiteUrl || null,
        yearsExperience:
          values.yearsExperience === "" || values.yearsExperience == null
            ? null
            : Number(values.yearsExperience),
        specialization: values.specialization || null,
        selfDescription: values.selfDescription || null,
        socialMediaUrls: {
          ...(values.socialLinkedin ? { linkedin: values.socialLinkedin } : {}),
          ...(values.socialInstagram ? { instagram: values.socialInstagram } : {}),
        },
        address: values.address && Object.values(values.address).some(Boolean)
          ? values.address
          : null,
      };
      const res = await axiosInstance.put("/interior-designers/profile", payload);
      setProfile(res);
      toast.success("Profile updated");
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating designer profile:", err);
      const detail = err?.response?.data?.detail || err.message || "Failed to update";
      toast.error(detail);
    }
  };

  /* --- Password --- */
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setPwdSubmitting(true);
    try {
      await setPassword(userInfo.username, newPassword);
      toast.success("Password changed");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Password change error:", err);
      toast.error("Failed to change password. Please try again.");
    } finally {
      setPwdSubmitting(false);
    }
  };

  /* --- Password strength rubric (shared with the developer Profile) --- */
  const rules = [
    { test: (pw) => pw.length >= 8, label: "At least 8 characters" },
    { test: (pw) => /[A-Z]/.test(pw), label: "One uppercase letter" },
    { test: (pw) => /[a-z]/.test(pw), label: "One lowercase letter" },
    { test: (pw) => /[0-9]/.test(pw), label: "One number" },
    { test: (pw) => /[^A-Za-z0-9]/.test(pw), label: "One special character" },
  ];
  const passed = rules.filter((r) => r.test(newPassword)).length;
  const strength = newPassword.length === 0 ? 0 : passed;
  const strengthMeta =
    strength <= 2
      ? { label: "Weak", color: "bg-rose-500", text: "text-rose-600" }
      : strength <= 3
      ? { label: "Fair", color: "bg-slate-400", text: "text-slate-600" }
      : strength <= 4
      ? { label: "Good", color: "bg-flamingo-400", text: "text-flamingo-600" }
      : { label: "Strong", color: "bg-flamingo-600", text: "text-flamingo-700" };
  const confirmMatches = confirmPassword.length > 0 && confirmPassword === newPassword;
  const confirmMismatched = confirmPassword.length > 0 && confirmPassword !== newPassword;

  /* --- Render ---- */
  const renderProfile = () => (
    <>
    <SectionCard
      title="Profile"
      subtitle="Personal details, company info and contact channels"
      action={
        <PrimaryButton onClick={() => setIsEditing(true)}>Edit profile</PrimaryButton>
      }
    >
      {profileLoading ? (
        <p className="text-sm text-slate-500">Loading profile…</p>
      ) : profileError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          {profileError}
        </div>
      ) : (
        <>
          {/* Avatar header */}
          <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
            <div className="w-16 h-16 rounded-2xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center font-semibold text-lg shadow-sm">
              {(profile?.name || "ID")
                .split(" ")
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-slate-900 truncate">
                {profile?.name || "Interior designer"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">
                {profile?.specialization || "Specialization not set"}
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              Personal details
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow icon={User} label="Full name" value={profile?.name} />
              <InfoRow icon={Mail} label="Email" value={profile?.email} />
              <InfoRow icon={Phone} label="Mobile" value={profile?.mobile} />
              <InfoRow
                icon={CalendarDays}
                label="Years of experience"
                value={
                  profile?.yearsExperience != null
                    ? `${profile.yearsExperience} years`
                    : null
                }
              />
              <InfoRow
                icon={MessageCircle}
                label="About"
                value={profile?.selfDescription}
                span
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              Company details
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow icon={Building2} label="Company name" value={profile?.companyName} />
              <InfoRow icon={Hash} label="GST number" value={profile?.gstNumber} />
              <InfoRow
                icon={Globe}
                label="Website"
                value={
                  profile?.websiteUrl ? (
                    <a
                      href={profile.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-flamingo-600 hover:text-flamingo-700 font-semibold"
                    >
                      {profile.websiteUrl}
                    </a>
                  ) : null
                }
              />
              <InfoRow
                icon={MessageCircle}
                label="Social links"
                value={
                  profile?.socialMediaUrls && Object.keys(profile.socialMediaUrls).length > 0
                    ? Object.entries(profile.socialMediaUrls)
                        .filter(([, v]) => v)
                        .map(([k]) => k)
                        .join(", ")
                    : null
                }
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
              Address
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow
                icon={MapPin}
                label="Street"
                value={
                  [profile?.address?.buildingNo, profile?.address?.streetName1, profile?.address?.streetName2]
                    .filter(Boolean)
                    .join(", ")
                }
                span
              />
              <InfoRow icon={MapPin} label="City" value={profile?.address?.city} />
              <InfoRow icon={MapPin} label="State" value={profile?.address?.state} />
              <InfoRow icon={MapPin} label="Pin code" value={profile?.address?.pinCode} />
              <InfoRow icon={MapPin} label="Country" value={profile?.address?.country} />
            </div>
          </div>
        </>
      )}
    </SectionCard>

    {/* Edit profile — canonical FormModal + Form + Field + yup validation.
        Schema-driven (profileSchema) so required-field errors and URL/number
        format checks surface inline before the request hits the BE. */}
    <FormModal
      open={isEditing}
      onClose={() => setIsEditing(false)}
      title="Edit profile"
      size="xl"
    >
      <Form
        schema={profileSchema}
        defaultValues={profileDefaults}
        onSubmit={async (values) => {
          await handleProfileSave(values);
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-3">
          <Field name="name" label="Full name" required />
          <Field name="email" label="Email" type="email" required />
          <Field name="mobile" label="Mobile" type="phone" required />
          <Field name="companyName" label="Company name" />
          <Field name="gstNumber" label="GST number" />
          <Field name="websiteUrl" label="Website" type="url" placeholder="https://…" />
          <Field name="yearsExperience" label="Years of experience" type="number" />
          <Field name="specialization" label="Specialization" />
          <Field
            name="selfDescription"
            label="About / bio"
            type="textarea"
            className="md:col-span-2"
          />
          <Field
            name="socialLinkedin"
            label="LinkedIn URL"
            placeholder="https://linkedin.com/in/…"
          />
          <Field
            name="socialInstagram"
            label="Instagram URL"
            placeholder="https://instagram.com/…"
          />
          <div className="col-span-1 md:col-span-2 mt-2 pt-3 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Address</h4>
            <AddressFields prefix="address" />
          </div>
        </div>
        <FormSubmit
          loadingLabel="Saving…"
          secondary={
            <OutlineButton type="button" onClick={() => setIsEditing(false)}>
              Cancel
            </OutlineButton>
          }
        >
          <Save size={14} className="mr-1.5" />
          Save changes
        </FormSubmit>
      </Form>
    </FormModal>
    </>
  );

  const renderPassword = () => (
    <SectionCard
      title="Change password"
      subtitle="Use a unique password you don't reuse on other sites"
      action={
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
          <KeyRound size={12} />
          Security
        </span>
      }
    >
      <form onSubmit={handlePasswordChange} className="space-y-5 max-w-xl">
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
            Current password
          </label>
          <CustomInput
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
            New password
          </label>
          <CustomInput
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          {newPassword.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${strengthMeta.color}`}
                    style={{ width: `${(strength / rules.length) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-semibold ${strengthMeta.text}`}>
                  {strengthMeta.label}
                </span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px]">
                {rules.map((r, i) => {
                  const ok = r.test(newPassword);
                  return (
                    <li
                      key={i}
                      className={`flex items-center gap-1.5 ${
                        ok ? "text-emerald-700" : "text-slate-500"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          ok ? "bg-emerald-500" : "bg-slate-300"
                        }`}
                      />
                      {r.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
            Confirm new password
          </label>
          <CustomInput
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {confirmMatches && (
            <p className="mt-1 text-[11px] text-emerald-600">Passwords match</p>
          )}
          {confirmMismatched && (
            <p className="mt-1 text-[11px] text-rose-600">Passwords don't match</p>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <PrimaryButton type="submit" disabled={pwdSubmitting}>
            {pwdSubmitting ? "Saving…" : "Change password"}
          </PrimaryButton>
        </div>
      </form>
    </SectionCard>
  );

  const renderSecurity = () => {
    const personaLabel = (userInfo?.persona || "").replace(/_/g, " ");
    const isActive = profile?.isActive !== false;
    const isLocked = !!profile?.isLocked;
    const created = profile?.createdDate
      ? new Date(profile.createdDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : null;
    return (
      <SectionCard
        title="Security"
        subtitle="Account state and persona"
        action={
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            <Shield size={12} />
            {personaLabel || "INTERIOR DESIGNER"}
          </span>
        }
      >
        <div className="rounded-xl border border-flamingo-200 bg-gradient-to-r from-flamingo-50 to-flamingo-50/30 p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-flamingo-500 text-white flex items-center justify-center flex-shrink-0">
            <BadgeCheck size={16} />
          </div>
          <div>
            <p className="text-sm font-semibold text-flamingo-900">Interior designer access</p>
            <p className="text-xs text-flamingo-700 mt-0.5">
              You have access to your own leads, designs, portfolio, and materials catalog.
              Interior designers don't participate in project-scoped RBAC permissions.
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
            Account state
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoRow
              icon={CalendarDays}
              label="Joined on"
              value={created}
            />
            <InfoRow
              icon={isActive ? BadgeCheck : AlertCircle}
              label="Active"
              value={
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                    isActive
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-rose-50 text-rose-700 border-rose-200"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isActive ? "bg-emerald-500" : "bg-rose-500"
                    }`}
                  />
                  {isActive ? "ACTIVE" : "INACTIVE"}
                </span>
              }
            />
            <InfoRow
              icon={isLocked ? Lock : BadgeCheck}
              label="Account lock"
              value={
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                    isLocked
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isLocked ? "bg-rose-500" : "bg-emerald-500"
                    }`}
                  />
                  {isLocked ? "LOCKED" : "UNLOCKED"}
                </span>
              }
            />
            {isLocked && profile?.accountLockedReason && (
              <InfoRow
                icon={AlertCircle}
                label="Lock reason"
                value={profile.accountLockedReason}
                span
              />
            )}
          </div>
        </div>
      </SectionCard>
    );
  };

  const renderActive = () => {
    if (activeSection === "password") return renderPassword();
    if (activeSection === "security") return renderSecurity();
    return renderProfile();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Hero band — slate gradient, same family as the rest of the app. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
          <SettingsIcon size={12} /> Account
        </p>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">Settings</h1>
        <p className="text-xs text-slate-300 mt-1">
          Manage your profile, password, and account security
        </p>
      </div>

      {/* Two-column layout — left nav + right content. */}
      <div className="flex flex-col lg:flex-row gap-4">
        <aside className="lg:w-64 shrink-0">
          <nav className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 flex flex-row lg:flex-col gap-1 overflow-x-auto">
            {SECTIONS.map(({ id, label, icon: Icon }) => {
              const active = activeSection === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSectionChange(id)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors text-left shrink-0 lg:w-full ${
                    active
                      ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={14} className="shrink-0" />
                  {label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">{renderActive()}</main>
      </div>
    </div>
  );
}

export default InteriorDesignerSettings;
